import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Plus, Trash2, Search, Save, Printer, Eye, Copy, ChevronDown,
  X, Check, AlertCircle, Building2, User as UserIcon, Package,
  ChevronRight, Calculator, FileText, RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';
import billingService from '../../../services/billingService';
import partyService from '../../../services/partyService';

// ─── Constants ────────────────────────────────────────────────────────────────
const DOC_META = {
  sales:            { label: 'Sales Invoice',     prefix: 'SI', partyLabel: 'Customer', partyType: 'customer' },
  purchase:         { label: 'Purchase Bill',     prefix: 'PI', partyLabel: 'Supplier', partyType: 'supplier' },
  sales_return:     { label: 'Sales Return',      prefix: 'SR', partyLabel: 'Customer', partyType: 'customer' },
  purchase_return:  { label: 'Purchase Return',   prefix: 'PR', partyLabel: 'Supplier', partyType: 'supplier' },
  credit_note:      { label: 'Credit Note',       prefix: 'CN', partyLabel: 'Customer', partyType: 'customer' },
  debit_note:       { label: 'Debit Note',        prefix: 'DN', partyLabel: 'Supplier', partyType: 'supplier' },
  quotation:        { label: 'Quotation',         prefix: 'QT', partyLabel: 'Customer', partyType: 'customer' },
  proforma:         { label: 'Proforma Invoice',  prefix: 'PFI',partyLabel: 'Customer', partyType: 'customer' },
  delivery_challan: { label: 'Delivery Challan',  prefix: 'DC', partyLabel: 'Customer', partyType: 'customer' },
  receipt:          { label: 'Receipt Voucher',   prefix: 'RCP',partyLabel: 'Party',    partyType: 'both' },
  payment:          { label: 'Payment Voucher',   prefix: 'PMT',partyLabel: 'Party',    partyType: 'both' },
  journal:          { label: 'Journal Entry',     prefix: 'JNL',partyLabel: 'Party',    partyType: 'both' },
};

const GST_RATES = [0, 0.1, 0.25, 1, 1.5, 3, 5, 6, 7.5, 9, 12, 14, 18, 28];
const UNITS = ['Nos', 'Pcs', 'Kg', 'Gm', 'Ltr', 'Ml', 'Mtr', 'Cm', 'Box', 'Bag', 'Set', 'Pair', 'Roll', 'Sheet', 'Ton'];
const STATE_CODES = {
  '33': 'Tamil Nadu', '29': 'Karnataka', '27': 'Maharashtra', '07': 'Delhi',
  '36': 'Telangana', '32': 'Kerala', '09': 'Uttar Pradesh', '24': 'Gujarat',
  '19': 'West Bengal', '21': 'Odisha', '06': 'Haryana', '03': 'Punjab',
  '08': 'Rajasthan', '23': 'Madhya Pradesh', '22': 'Chhattisgarh', '20': 'Jharkhand'
};
const POS_OPTIONS = Object.entries(STATE_CODES).map(([code, name]) => `${code}-${name}`);
const COMPANY_STATE_CODE = '33';
const COMPANY_STATE = 'Tamil Nadu';

const emptyItem = () => ({
  _key: Math.random().toString(36).slice(2),
  description: '', hsnCode: '', sacCode: '', unit: 'Nos',
  quantity: 1, unitPrice: 0, mrp: 0, discount: 0, gstRate: 18,
  taxableAmount: 0, cgstRate: 0, cgstAmount: 0, sgstRate: 0, sgstAmount: 0,
  igstRate: 0, igstAmount: 0, cessRate: 0, cessAmount: 0, amount: 0,
  batch: '', serialNo: '', warehouse: ''
});

const fmt = (n) => (n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─── Party Quick-Add Modal ────────────────────────────────────────────────────
function PartyModal({ type, onSave, onClose }) {
  const [form, setForm] = useState({ type: type === 'both' ? 'customer' : type, name: '', gstin: '', mobile: '', email: '',
    state: COMPANY_STATE, stateCode: COMPANY_STATE_CODE, placeOfSupply: `${COMPANY_STATE_CODE}-${COMPANY_STATE}` });
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    if (!form.name) return toast.error('Party name is required');
    setSaving(true);
    try {
      const res = await partyService.create(form);
      toast.success(`${form.name} added!`);
      onSave(res.data);
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <h3 className="font-bold text-sm text-slate-900">Add New Party</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 text-slate-500"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-4 space-y-3">
          <div><label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Type</label>
            <select value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value}))}
              className="w-full text-xs font-semibold px-3 py-2 rounded-xl border border-slate-200 bg-slate-50">
              <option value="customer">Customer</option><option value="supplier">Supplier</option><option value="both">Both</option>
            </select>
          </div>
          <div><label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Name *</label>
            <input autoFocus value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))}
              className="w-full text-xs font-bold px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 outline-none focus:border-purple-400" placeholder="Party name" />
          </div>
          <div><label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">GSTIN</label>
            <input value={form.gstin} onChange={e=>setForm(f=>({...f,gstin:e.target.value.toUpperCase()}))}
              className="w-full text-xs font-mono font-bold px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 outline-none" placeholder="e.g. 33AAAPZ1234A1Z1" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Mobile</label>
              <input value={form.mobile} onChange={e=>setForm(f=>({...f,mobile:e.target.value}))}
                className="w-full text-xs font-semibold px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 outline-none" placeholder="Mobile" />
            </div>
            <div><label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Email</label>
              <input value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))}
                className="w-full text-xs font-semibold px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 outline-none" placeholder="Email" />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="px-5 py-2 text-xs font-bold bg-purple-600 hover:bg-purple-700 text-white rounded-xl disabled:opacity-60">
            {saving ? 'Saving…' : 'Save Party'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main InvoiceForm Component ───────────────────────────────────────────────
export default function InvoiceForm() {
  const navigate = useNavigate();
  const { id, type: typeParam } = useParams();
  const isEdit = Boolean(id);

  const [docType, setDocType]       = useState(typeParam || 'sales');
  const meta = DOC_META[docType] || DOC_META.sales;

  const [saving, setSaving]         = useState(false);
  const [loading, setLoading]       = useState(isEdit);
  const [showPartyModal, setShowPartyModal] = useState(false);
  const [parties, setParties]       = useState([]);
  const [partySearch, setPartySearch] = useState('');
  const [showPartyDropdown, setShowPartyDropdown] = useState(false);
  const partyRef = useRef(null);

  const [form, setForm] = useState({
    billingType: typeParam || 'sales',
    date: new Date().toISOString().split('T')[0],
    dueDate: '', validUntil: '',
    party: null,
    partyName: '', partyGstin: '', partyPhone: '', partyEmail: '',
    customerGstin: '', vendorGstin: '',
    billingAddress: { line1: '', city: '', state: '', pincode: '' },
    shippingAddress: { line1: '', city: '', state: '', pincode: '' },
    placeOfSupply: `${COMPANY_STATE_CODE}-${COMPANY_STATE}`,
    isInterState: false,
    supplyType: 'regular',
    reverseCharge: 'N',
    items: [emptyItem()],
    otherCharges: 0, otherChargesDesc: 'Freight / Other Charges',
    notes: '', terms: '',
    paymentMethod: 'bank_transfer',
    paidAmount: 0,
    status: 'draft',
    referenceNumber: '',
  });

  // Derived totals
  const [totals, setTotals] = useState({
    subtotal: 0, discountAmount: 0, taxableValue: 0,
    cgstTotal: 0, sgstTotal: 0, igstTotal: 0, cessTotal: 0, taxAmount: 0,
    otherCharges: 0, roundOff: 0, grandTotal: 0, balance: 0
  });

  // ── Load parties ───────────────────────────────────────────────────────────
  useEffect(() => {
    partyService.getAll({ type: meta.partyType === 'both' ? undefined : meta.partyType, limit: 500 })
      .then(r => setParties(r.data || []))
      .catch(() => {});
  }, [meta.partyType]);

  // ── Load existing bill for edit ────────────────────────────────────────────
  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    billingService.getBillingById(id).then(res => {
      const b = res.data;
      setDocType(b.billingType);
      setForm({
        ...b,
        date: b.date ? b.date.split('T')[0] : new Date().toISOString().split('T')[0],
        dueDate: b.dueDate ? b.dueDate.split('T')[0] : '',
        validUntil: b.validUntil ? b.validUntil.split('T')[0] : '',
        items: (b.items || [emptyItem()]).map(item => ({ ...item, _key: item._id || Math.random().toString(36).slice(2) }))
      });
    }).catch(() => toast.error('Failed to load document'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  // ── Calculate Totals ───────────────────────────────────────────────────────
  useEffect(() => {
    const isInter = form.isInterState;
    let subtotal = 0, discountAmount = 0, cgstTotal = 0, sgstTotal = 0, igstTotal = 0, cessTotal = 0;

    const updatedItems = form.items.map(item => {
      const gross = (item.quantity || 0) * (item.unitPrice || 0);
      const discAmt = (gross * (item.discount || 0)) / 100;
      const taxable = gross - discAmt;
      const gstRate = item.gstRate || 0;
      let cgstAmt = 0, sgstAmt = 0, igstAmt = 0, cessAmt = 0;

      if (isInter) {
        igstAmt = parseFloat(((taxable * gstRate) / 100).toFixed(2));
        igstTotal += igstAmt;
      } else {
        cgstAmt = parseFloat(((taxable * gstRate / 2) / 100).toFixed(2));
        sgstAmt = parseFloat(((taxable * gstRate / 2) / 100).toFixed(2));
        cgstTotal += cgstAmt;
        sgstTotal += sgstAmt;
      }
      cessAmt = parseFloat(((taxable * (item.cessRate || 0)) / 100).toFixed(2));
      cessTotal += cessAmt;
      subtotal += taxable;
      discountAmount += discAmt;

      return {
        ...item,
        taxableAmount: parseFloat(taxable.toFixed(2)),
        cgstRate: isInter ? 0 : gstRate / 2,
        cgstAmount: cgstAmt,
        sgstRate: isInter ? 0 : gstRate / 2,
        sgstAmount: sgstAmt,
        igstRate: isInter ? gstRate : 0,
        igstAmount: igstAmt,
        cessAmount: cessAmt,
        amount: parseFloat((taxable + cgstAmt + sgstAmt + igstAmt + cessAmt).toFixed(2))
      };
    });

    const taxAmount = cgstTotal + sgstTotal + igstTotal + cessTotal;
    const preRound = subtotal + taxAmount + (parseFloat(form.otherCharges) || 0);
    const grandTotal = Math.round(preRound);
    const roundOff = parseFloat((grandTotal - preRound).toFixed(2));

    setForm(f => ({ ...f, items: updatedItems }));
    setTotals({
      subtotal: parseFloat(subtotal.toFixed(2)),
      discountAmount: parseFloat(discountAmount.toFixed(2)),
      taxableValue: parseFloat(subtotal.toFixed(2)),
      cgstTotal: parseFloat(cgstTotal.toFixed(2)),
      sgstTotal: parseFloat(sgstTotal.toFixed(2)),
      igstTotal: parseFloat(igstTotal.toFixed(2)),
      cessTotal: parseFloat(cessTotal.toFixed(2)),
      taxAmount: parseFloat(taxAmount.toFixed(2)),
      otherCharges: parseFloat(form.otherCharges) || 0,
      roundOff,
      grandTotal,
      balance: parseFloat((grandTotal - (parseFloat(form.paidAmount) || 0)).toFixed(2))
    });
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(form.items.map(i => ({ q: i.quantity, u: i.unitPrice, d: i.discount, g: i.gstRate, c: i.cessRate }))),
    form.isInterState, form.otherCharges, form.paidAmount
  ]);

  // ── Keyboard Shortcuts ────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey)) {
        if (e.key === 's') { e.preventDefault(); handleSave('saved'); }
        if (e.key === 'p') { e.preventDefault(); handleSave('saved', true); }
        if (e.key === 'Enter') { e.preventDefault(); addItem(); }
      }
      if (e.key === 'Escape') navigate(-1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [form]);

  // ── Party selection ────────────────────────────────────────────────────────
  const filteredParties = parties.filter(p =>
    !partySearch || p.name.toLowerCase().includes(partySearch.toLowerCase()) ||
    (p.gstin || '').includes(partySearch.toUpperCase()) ||
    (p.mobile || '').includes(partySearch)
  ).slice(0, 20);

  const selectParty = (p) => {
    const isInter = p.stateCode && p.stateCode !== COMPANY_STATE_CODE;
    setForm(f => ({
      ...f,
      party: p._id,
      partyName: p.name,
      partyGstin: p.gstin || '',
      partyPhone: p.mobile || '',
      partyEmail: p.email || '',
      customerGstin: p.type === 'customer' || p.type === 'both' ? (p.gstin || '') : f.customerGstin,
      vendorGstin: p.type === 'supplier' || p.type === 'both' ? (p.gstin || '') : f.vendorGstin,
      placeOfSupply: p.placeOfSupply || `${p.stateCode || COMPANY_STATE_CODE}-${p.state || COMPANY_STATE}`,
      isInterState: isInter,
      billingAddress: p.billingAddress || f.billingAddress,
    }));
    setPartySearch(p.name);
    setShowPartyDropdown(false);
  };

  // ── Item helpers ──────────────────────────────────────────────────────────
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, emptyItem()] }));

  const removeItem = (key) => {
    if (form.items.length === 1) return;
    setForm(f => ({ ...f, items: f.items.filter(i => i._key !== key) }));
  };

  const updateItem = (key, field, value) => {
    setForm(f => ({
      ...f,
      items: f.items.map(item => item._key === key ? { ...item, [field]: value } : item)
    }));
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async (status = 'saved', andPrint = false) => {
    if (!form.partyName && !form.party) {
      toast.error('Please select or enter a party');
      return;
    }
    if (form.items.some(i => !i.description)) {
      toast.error('All items must have a description');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        billingType: docType,
        status,
        taxableValue: totals.taxableValue,
        subtotal: totals.subtotal,
        discountAmount: totals.discountAmount,
        cgstTotal: totals.cgstTotal,
        sgstTotal: totals.sgstTotal,
        igstTotal: totals.igstTotal,
        cessTotal: totals.cessTotal,
        taxAmount: totals.taxAmount,
        otherCharges: totals.otherCharges,
        roundOff: totals.roundOff,
        totalAmount: totals.grandTotal,
        balanceAmount: totals.balance,
        paidAmount: parseFloat(form.paidAmount) || 0,
        gstBreakdown: {
          totalTaxable: totals.taxableValue,
          totalCgst: totals.cgstTotal,
          totalSgst: totals.sgstTotal,
          totalIgst: totals.igstTotal,
          totalCess: totals.cessTotal,
          totalTax: totals.taxAmount,
          isInterState: form.isInterState
        }
      };

      let result;
      if (isEdit) {
        result = await billingService.updateBilling(id, payload);
      } else {
        result = await billingService.createBilling(payload);
      }

      toast.success(status === 'draft' ? 'Saved as Draft' : `${meta.label} saved!`);
      if (andPrint) {
        window.open(`/admin/billing/${result.data._id}`, '_blank');
      }
      navigate(`/admin/billing/${result.data._id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-4 border-purple-600 border-t-transparent" />
    </div>
  );

  return (
    <div className="max-w-[1200px] mx-auto space-y-4 pb-10">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white border border-slate-200 rounded-2xl px-5 py-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-500">
            <X className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-base font-black text-slate-900">{isEdit ? `Edit ${meta.label}` : `New ${meta.label}`}</h1>
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-0.5">
              Ctrl+S Save · Ctrl+P Save&Print · Ctrl+Enter Add Item · Esc Back
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Doc type selector */}
          <select value={docType} onChange={e => { setDocType(e.target.value); setForm(f => ({ ...f, billingType: e.target.value })); }}
            className="text-xs font-bold px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-slate-700">
            {Object.entries(DOC_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <button onClick={() => handleSave('draft')} disabled={saving}
            className="px-3 py-2 text-xs font-bold rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-60">
            Save Draft
          </button>
          <button onClick={() => handleSave('saved')} disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-xl bg-purple-600 hover:bg-purple-700 text-white shadow-sm disabled:opacity-60">
            <Save className="w-3.5 h-3.5" />
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button onClick={() => handleSave('saved', true)} disabled={saving}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-xl bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-60">
            <Printer className="w-3.5 h-3.5" /> Save & Print
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* ── Left 2/3 — Main Form ─────────────────────────────────────── */}
        <div className="lg:col-span-2 space-y-4">

          {/* Party & Dates */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
            <h3 className="text-xs font-black text-slate-500 uppercase tracking-wider">{meta.partyLabel} Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Party Search */}
              <div className="relative sm:col-span-2" ref={partyRef}>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">{meta.partyLabel} *</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                    <input
                      value={partySearch}
                      onChange={e => { setPartySearch(e.target.value); setForm(f => ({ ...f, partyName: e.target.value, party: null })); setShowPartyDropdown(true); }}
                      onFocus={() => setShowPartyDropdown(true)}
                      placeholder={`Search or type ${meta.partyLabel.toLowerCase()} name…`}
                      className="w-full pl-9 pr-3 py-2.5 text-sm font-semibold rounded-xl border border-slate-200 bg-slate-50 outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-200"
                    />
                    {showPartyDropdown && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-slate-200 shadow-xl z-20 max-h-52 overflow-y-auto">
                        {filteredParties.length === 0 ? (
                          <div className="px-4 py-3 text-xs text-slate-500">No party found. Add new?</div>
                        ) : filteredParties.map(p => (
                          <button key={p._id} onClick={() => selectParty(p)}
                            className="w-full text-left px-4 py-2.5 hover:bg-slate-50 border-b border-slate-50 last:border-0">
                            <p className="font-bold text-sm text-slate-900">{p.name}</p>
                            <p className="font-mono text-[10px] text-slate-400">{p.gstin || 'Unregistered'} · {p.mobile || ''}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button onClick={() => setShowPartyModal(true)}
                    className="p-2.5 rounded-xl border border-dashed border-purple-300 bg-purple-50 text-purple-600 hover:bg-purple-100" title="Add new party">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                {form.partyGstin && (
                  <div className="mt-2 flex items-center gap-2 text-[10px] font-mono font-bold text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-lg">
                    <Check className="w-3.5 h-3.5" /> GSTIN: {form.partyGstin}
                    <span className="ml-2 font-sans font-bold text-emerald-600">{form.isInterState ? '→ IGST Applicable' : '→ CGST+SGST Applicable'}</span>
                  </div>
                )}
              </div>

              {/* Invoice Date */}
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Invoice Date *</label>
                <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                  className="w-full px-3 py-2.5 text-sm font-semibold rounded-xl border border-slate-200 bg-slate-50 outline-none focus:border-purple-400" />
              </div>

              {/* Due Date */}
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Due Date</label>
                <input type="date" value={form.dueDate} onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))}
                  className="w-full px-3 py-2.5 text-sm font-semibold rounded-xl border border-slate-200 bg-slate-50 outline-none focus:border-purple-400" />
              </div>

              {/* Place of Supply */}
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Place of Supply</label>
                <select value={form.placeOfSupply}
                  onChange={e => {
                    const code = e.target.value.split('-')[0];
                    setForm(f => ({ ...f, placeOfSupply: e.target.value, isInterState: code !== COMPANY_STATE_CODE }));
                  }}
                  className="w-full px-3 py-2.5 text-xs font-semibold rounded-xl border border-slate-200 bg-slate-50 outline-none focus:border-purple-400">
                  {POS_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>

              {/* Supply Type */}
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Supply Type</label>
                <select value={form.supplyType} onChange={e => setForm(f => ({ ...f, supplyType: e.target.value }))}
                  className="w-full px-3 py-2.5 text-xs font-semibold rounded-xl border border-slate-200 bg-slate-50 outline-none focus:border-purple-400">
                  <option value="regular">Regular</option>
                  <option value="sez">SEZ with Pay</option>
                  <option value="sez_without_pay">SEZ without Pay</option>
                  <option value="export_with_pay">Export with Pay</option>
                  <option value="export_without_pay">Export without Pay</option>
                  <option value="deemed_export">Deemed Export</option>
                  <option value="exempt">Exempt</option>
                  <option value="nil_rated">Nil Rated</option>
                  <option value="non_gst">Non-GST</option>
                </select>
              </div>

              {/* Reverse Charge */}
              <div className="flex items-center gap-3 self-end pb-1">
                <label className="text-xs font-bold text-slate-700">Reverse Charge (RCM)</label>
                <button onClick={() => setForm(f => ({ ...f, reverseCharge: f.reverseCharge === 'Y' ? 'N' : 'Y' }))}
                  className={`w-12 h-6 rounded-full transition-colors ${form.reverseCharge === 'Y' ? 'bg-orange-500' : 'bg-slate-200'}`}>
                  <span className={`block w-5 h-5 bg-white rounded-full shadow-sm transition-transform mx-0.5 ${form.reverseCharge === 'Y' ? 'translate-x-6' : ''}`} />
                </button>
                <span className="text-xs font-bold text-slate-500">{form.reverseCharge === 'Y' ? 'YES' : 'NO'}</span>
              </div>

              {/* Reference */}
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Ref. Invoice / PO No.</label>
                <input value={form.referenceNumber} onChange={e => setForm(f => ({ ...f, referenceNumber: e.target.value }))}
                  placeholder="Reference number"
                  className="w-full px-3 py-2.5 text-sm font-semibold rounded-xl border border-slate-200 bg-slate-50 outline-none focus:border-purple-400" />
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <h3 className="text-xs font-black text-slate-500 uppercase tracking-wider">Line Items</h3>
              <span className="text-[10px] text-slate-400 font-medium">Ctrl+Enter to add row</span>
            </div>

            {/* Table header */}
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="px-3 py-2 text-left font-bold text-slate-400 uppercase w-6">#</th>
                    <th className="px-3 py-2 text-left font-bold text-slate-400 uppercase min-w-[200px]">Item / Service</th>
                    <th className="px-3 py-2 text-left font-bold text-slate-400 uppercase w-24">HSN/SAC</th>
                    <th className="px-3 py-2 text-left font-bold text-slate-400 uppercase w-16">Unit</th>
                    <th className="px-3 py-2 text-right font-bold text-slate-400 uppercase w-16">Qty</th>
                    <th className="px-3 py-2 text-right font-bold text-slate-400 uppercase w-24">Rate (₹)</th>
                    <th className="px-3 py-2 text-right font-bold text-slate-400 uppercase w-16">Disc %</th>
                    <th className="px-3 py-2 text-right font-bold text-slate-400 uppercase w-24">Taxable (₹)</th>
                    <th className="px-3 py-2 text-right font-bold text-slate-400 uppercase w-20">GST %</th>
                    <th className="px-3 py-2 text-right font-bold text-slate-400 uppercase w-24">Amount (₹)</th>
                    <th className="px-3 py-2 w-8" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {form.items.map((item, idx) => (
                    <tr key={item._key} className="group hover:bg-slate-50/50">
                      <td className="px-3 py-2 text-slate-400 font-mono font-bold">{idx + 1}</td>
                      <td className="px-3 py-2">
                        <input value={item.description}
                          onChange={e => updateItem(item._key, 'description', e.target.value)}
                          placeholder="Item description"
                          className="w-full text-sm font-semibold text-slate-900 outline-none bg-transparent placeholder-slate-300 border-b border-transparent focus:border-purple-300" />
                      </td>
                      <td className="px-3 py-2">
                        <input value={item.hsnCode}
                          onChange={e => updateItem(item._key, 'hsnCode', e.target.value)}
                          placeholder="8471"
                          className="w-full font-mono text-slate-700 outline-none bg-transparent border-b border-transparent focus:border-purple-300" />
                      </td>
                      <td className="px-3 py-2">
                        <select value={item.unit} onChange={e => updateItem(item._key, 'unit', e.target.value)}
                          className="w-full font-semibold text-slate-700 outline-none bg-transparent">
                          {UNITS.map(u => <option key={u}>{u}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <input type="number" min="0" value={item.quantity}
                          onChange={e => updateItem(item._key, 'quantity', parseFloat(e.target.value) || 0)}
                          className="w-full text-right font-mono font-bold text-slate-900 outline-none bg-transparent border-b border-transparent focus:border-purple-300" />
                      </td>
                      <td className="px-3 py-2">
                        <input type="number" min="0" step="0.01" value={item.unitPrice}
                          onChange={e => updateItem(item._key, 'unitPrice', parseFloat(e.target.value) || 0)}
                          className="w-full text-right font-mono font-bold text-slate-900 outline-none bg-transparent border-b border-transparent focus:border-purple-300" />
                      </td>
                      <td className="px-3 py-2">
                        <input type="number" min="0" max="100" value={item.discount}
                          onChange={e => updateItem(item._key, 'discount', parseFloat(e.target.value) || 0)}
                          className="w-full text-right font-mono font-semibold text-slate-700 outline-none bg-transparent border-b border-transparent focus:border-purple-300" />
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-slate-700">
                        {fmt(item.taxableAmount)}
                      </td>
                      <td className="px-3 py-2">
                        <select value={item.gstRate} onChange={e => updateItem(item._key, 'gstRate', parseFloat(e.target.value))}
                          className="w-full font-mono font-bold text-slate-700 outline-none bg-transparent text-right">
                          {GST_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-black text-slate-900">
                        {fmt(item.amount)}
                      </td>
                      <td className="px-3 py-2">
                        <button onClick={() => removeItem(item._key)}
                          className="opacity-0 group-hover:opacity-100 p-1 rounded-lg hover:bg-red-50 text-red-400 transition-opacity">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-5 py-3 border-t border-slate-100">
              <button onClick={addItem}
                className="flex items-center gap-2 text-xs font-bold text-purple-600 hover:text-purple-700 hover:bg-purple-50 px-3 py-1.5 rounded-lg transition-colors">
                <Plus className="w-3.5 h-3.5" /> Add Item
              </button>
            </div>
          </div>

          {/* Notes & Terms */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 rounded-2xl p-4">
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-2">Notes / Remarks</label>
              <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={3} placeholder="Internal notes or customer message…"
                className="w-full text-xs font-medium text-slate-700 placeholder-slate-300 outline-none resize-none bg-transparent" />
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-4">
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-2">Terms & Conditions</label>
              <textarea value={form.terms} onChange={e => setForm(f => ({ ...f, terms: e.target.value }))}
                rows={3} placeholder="Payment terms, warranty, etc…"
                className="w-full text-xs font-medium text-slate-700 placeholder-slate-300 outline-none resize-none bg-transparent" />
            </div>
          </div>
        </div>

        {/* ── Right 1/3 — Summary Panel ────────────────────────────────── */}
        <div className="space-y-4">
          {/* Tax Summary */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3 sticky top-4">
            <h3 className="text-xs font-black text-slate-500 uppercase tracking-wider flex items-center gap-2">
              <Calculator className="w-3.5 h-3.5" /> Tax Summary
            </h3>
            <div className="space-y-2 text-xs font-semibold">
              <div className="flex justify-between text-slate-600">
                <span>Taxable Value</span>
                <span className="font-mono font-bold text-slate-900">₹{fmt(totals.taxableValue)}</span>
              </div>
              {!form.isInterState ? (
                <>
                  <div className="flex justify-between text-slate-500">
                    <span>CGST</span>
                    <span className="font-mono">₹{fmt(totals.cgstTotal)}</span>
                  </div>
                  <div className="flex justify-between text-slate-500">
                    <span>SGST</span>
                    <span className="font-mono">₹{fmt(totals.sgstTotal)}</span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between text-slate-500">
                  <span>IGST</span>
                  <span className="font-mono">₹{fmt(totals.igstTotal)}</span>
                </div>
              )}
              {totals.cessTotal > 0 && (
                <div className="flex justify-between text-slate-500">
                  <span>Cess</span>
                  <span className="font-mono">₹{fmt(totals.cessTotal)}</span>
                </div>
              )}
              <div className="flex justify-between text-slate-600 pt-1 border-t border-slate-100">
                <span>Total Tax</span>
                <span className="font-mono font-bold text-slate-900">₹{fmt(totals.taxAmount)}</span>
              </div>

              {/* Other Charges */}
              <div className="pt-2 border-t border-slate-100">
                <div className="flex items-center gap-2 mb-1">
                  <input value={form.otherChargesDesc}
                    onChange={e => setForm(f => ({ ...f, otherChargesDesc: e.target.value }))}
                    className="flex-1 text-slate-500 outline-none bg-transparent text-xs" />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 shrink-0">₹</span>
                  <input type="number" min="0" step="0.01" value={form.otherCharges}
                    onChange={e => setForm(f => ({ ...f, otherCharges: e.target.value }))}
                    className="w-full text-right font-mono font-semibold text-slate-700 outline-none bg-transparent border-b border-slate-200 focus:border-purple-400" />
                </div>
              </div>

              {totals.roundOff !== 0 && (
                <div className="flex justify-between text-slate-400 text-[10px]">
                  <span>Round Off</span>
                  <span className="font-mono">{totals.roundOff > 0 ? '+' : ''}₹{fmt(totals.roundOff)}</span>
                </div>
              )}

              {/* Grand Total */}
              <div className="flex justify-between items-center py-2 border-t-2 border-slate-900 mt-2">
                <span className="font-black text-sm text-slate-900">Grand Total</span>
                <span className="font-mono font-black text-lg text-purple-700">₹{fmt(totals.grandTotal)}</span>
              </div>
            </div>

            {/* Payment */}
            <div className="pt-3 border-t border-slate-100 space-y-3">
              <h4 className="text-[10px] font-black text-slate-500 uppercase">Payment</h4>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 mb-1">Mode</label>
                <select value={form.paymentMethod} onChange={e => setForm(f => ({ ...f, paymentMethod: e.target.value }))}
                  className="w-full text-xs font-semibold px-3 py-2 rounded-xl border border-slate-200 bg-slate-50">
                  <option value="">— Select Mode —</option>
                  <option value="bank_transfer">Bank Transfer / NEFT</option>
                  <option value="upi">UPI</option>
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="cheque">Cheque</option>
                  <option value="credit">On Credit</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 mb-1">Paid Amount (₹)</label>
                <input type="number" min="0" step="0.01" value={form.paidAmount}
                  onChange={e => setForm(f => ({ ...f, paidAmount: e.target.value }))}
                  className="w-full px-3 py-2 text-sm font-mono font-bold rounded-xl border border-slate-200 bg-slate-50 outline-none focus:border-purple-400" />
              </div>
              <div className="flex justify-between text-xs font-bold">
                <span className="text-slate-600">Balance Due</span>
                <span className={`font-mono ${totals.balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                  ₹{fmt(totals.balance)}
                </span>
              </div>
            </div>

            {/* GST flag */}
            <div className={`flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-bold mt-2
              ${form.isInterState ? 'bg-indigo-50 text-indigo-700' : 'bg-emerald-50 text-emerald-700'}`}>
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              {form.isInterState ? 'Interstate → IGST Applied' : 'Intrastate → CGST+SGST Applied'}
            </div>
          </div>
        </div>
      </div>

      {/* Party Modal */}
      {showPartyModal && (
        <PartyModal
          type={meta.partyType}
          onSave={(p) => { selectParty(p); setShowPartyModal(false); setParties(prev => [...prev, p]); }}
          onClose={() => setShowPartyModal(false)}
        />
      )}

      {/* Click outside party dropdown */}
      {showPartyDropdown && <div className="fixed inset-0 z-10" onClick={() => setShowPartyDropdown(false)} />}
    </div>
  );
}
